import { type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Spin } from 'antd';
import { useAuth } from '@/contexts/AuthContext';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { usePermissions } from '@/hooks/usePermissions';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredPermission?: string;
  // When true, the route also requires an active merchant context: the user has
  // a merchant assigned to their account, or is impersonating one. Used for
  // merchant-scoped features (e.g. Address Book) that are meaningless without a
  // merchant to scope to.
  requireMerchantContext?: boolean;
  fallbackPath?: string;
}

export function ProtectedRoute({
  children,
  requiredPermission,
  requireMerchantContext = false,
  fallbackPath = '/home'
}: ProtectedRouteProps) {
  const { isAuthenticated, user } = useAuth();
  const { impersonatedMerchantCode } = useImpersonation();
  const { hasPermission, loading: permissionsLoading } = usePermissions();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Wait for permissions to load before checking
  if (permissionsLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <Spin size="large" />
      </div>
    );
  }

  // Check permission requirement
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to={fallbackPath} replace />;
  }

  // Check merchant-context requirement (assigned merchant or impersonating).
  if (requireMerchantContext) {
    const hasMerchantContext = !!user?.profile?.merchantId || !!impersonatedMerchantCode;
    if (!hasMerchantContext) {
      return <Navigate to={fallbackPath} replace />;
    }
  }

  return <>{children}</>;
}

